import {
  EXCEL_EXPORT_FIELDS_FETCH_START,
  EXCEL_EXPORT_FIELDS_FETCH_SUCCESS,
  EXCEL_EXPORT_FIELDS_FETCH_FAILURE,
  EXCEL_EXPORT_PREVIEW_START,
  EXCEL_EXPORT_PREVIEW_SUCCESS,
  EXCEL_EXPORT_PREVIEW_FAILURE,
  EXCEL_EXPORT_GENERATE_START,
  EXCEL_EXPORT_GENERATE_SUCCESS,
  EXCEL_EXPORT_GENERATE_FAILURE,
  EXCEL_EXPORT_DOWNLOAD_START,
  EXCEL_EXPORT_DOWNLOAD_SUCCESS,
  EXCEL_EXPORT_DOWNLOAD_FAILURE,
  EXCEL_EXPORT_HISTORY_FETCH_START,
  EXCEL_EXPORT_HISTORY_FETCH_SUCCESS,
  EXCEL_EXPORT_HISTORY_FETCH_FAILURE,
  EXCEL_EXPORT_STATISTICS_FETCH_START,
  EXCEL_EXPORT_STATISTICS_FETCH_SUCCESS,
  EXCEL_EXPORT_STATISTICS_FETCH_FAILURE,
  EXCEL_EXPORT_RESET_STATE,
  EXCEL_EXPORT_SET_CURRENT_STEP,
  EXCEL_EXPORT_UPDATE_FIELDS,
  EXCEL_EXPORT_UPDATE_FILTERS,
  EXCEL_EXPORT_UPDATE_OPTIONS
} from '../actions/constants';

const initialState = {
  // UI State
  currentStep: 1,
  isLoading: false,
  error: null,
  
  // Entity Fields State
  availableFields: {},
  relationships: {},
  fieldsLoading: false,
  
  // Selected Fields State
  selectedFields: [],
  fieldCategories: {
    basic: [],
    advanced: [],
    relationships: []
  },
  
  // Filters State
  filters: [],
  filterPresets: [],
  activePreset: null,
  
  // Preview State
  previewData: [],
  previewStatistics: {
    totalCount: 0,
    previewCount: 0,
    estimatedSize: '0 KB'
  },
  previewLoading: false,
  
  // Export Generation State
  activeExports: {}, // exportId -> export details
  exportProgress: {}, // exportId -> progress percentage
  
  // Export Options State
  exportOptions: {
    format: 'xlsx',
    fileName: '',
    includeHeaders: true,
    includeMetadata: false,
    compression: false,
    maxRecords: 10000
  },
  
  // Validation State
  validation: {
    isValid: true,
    errors: [],
    warnings: []
  },
  
  // Size Estimation State
  sizeEstimation: {
    bytes: 0,
    text: '0 KB',
    warning: false
  },
  
  // History State
  exportHistory: [],
  historyPagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNext: false,
    hasPrev: false
  },
  historyLoading: false,
  
  // Statistics State
  statistics: {
    totalExports: 0,
    totalRecordsExported: 0,
    averageExportSize: 0,
    popularFormats: [],
    recentActivity: []
  },
  statisticsLoading: false,
  
  // Download State
  downloadProgress: {},
  downloadErrors: {}
};

const excelExportReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fields Actions
    case EXCEL_EXPORT_FIELDS_FETCH_START:
      return {
        ...state,
        fieldsLoading: true,
        error: null
      };
      
    case EXCEL_EXPORT_FIELDS_FETCH_SUCCESS:
      return {
        ...state,
        fieldsLoading: false,
        error: null,
        availableFields: {
          ...state.availableFields,
          [action.payload.entityType]: action.payload.fields
        },
        relationships: {
          ...state.relationships,
          [action.payload.entityType]: action.payload.relationships
        }
      };
      
    case EXCEL_EXPORT_FIELDS_FETCH_FAILURE:
      return {
        ...state,
        fieldsLoading: false,
        error: action.payload
      };
      
    // Preview Actions
    case EXCEL_EXPORT_PREVIEW_START:
      return {
        ...state,
        previewLoading: true,
        error: null
      };
      
    case EXCEL_EXPORT_PREVIEW_SUCCESS:
      return {
        ...state,
        previewLoading: false,
        error: null,
        previewData: action.payload.data,
        previewStatistics: {
          totalCount: action.payload.totalCount,
          previewCount: action.payload.data.length,
          estimatedSize: action.payload.statistics?.estimatedSize || '0 KB'
        }
      };
      
    case EXCEL_EXPORT_PREVIEW_FAILURE:
      return {
        ...state,
        previewLoading: false,
        error: action.payload
      };
      
    // Generate Actions
    case EXCEL_EXPORT_GENERATE_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
      
    case EXCEL_EXPORT_GENERATE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        activeExports: {
          ...state.activeExports,
          [action.payload.exportId]: {
            exportId: action.payload.exportId,
            status: action.payload.status,
            fileName: action.payload.fileName,
            startedAt: new Date().toISOString()
          }
        },
        exportProgress: {
          ...state.exportProgress,
          [action.payload.exportId]: 0
        }
      };
      
    case EXCEL_EXPORT_GENERATE_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
      
    // Progress Updates
    case 'EXCEL_EXPORT_PROGRESS_UPDATE':
      return {
        ...state,
        exportProgress: {
          ...state.exportProgress,
          [action.payload.exportId]: action.payload.progress.percentage || 0
        }
      };
      
    case 'EXCEL_EXPORT_COMPLETED':
      return {
        ...state,
        activeExports: {
          ...state.activeExports,
          [action.payload.exportId]: {
            ...state.activeExports[action.payload.exportId],
            status: 'COMPLETED',
            completedAt: new Date().toISOString(),
            results: action.payload.results
          }
        },
        exportProgress: {
          ...state.exportProgress,
          [action.payload.exportId]: 100
        }
      };
      
    // Download Actions
    case EXCEL_EXPORT_DOWNLOAD_START:
      return {
        ...state,
        downloadProgress: {
          ...state.downloadProgress,
          [action.payload?.exportId]: 0
        },
        downloadErrors: {
          ...state.downloadErrors,
          [action.payload?.exportId]: null
        }
      };
      
    case EXCEL_EXPORT_DOWNLOAD_SUCCESS:
      return {
        ...state,
        downloadProgress: {
          ...state.downloadProgress,
          [action.payload.exportId]: 100
        },
        downloadErrors: {
          ...state.downloadErrors,
          [action.payload.exportId]: null
        }
      };
      
    case EXCEL_EXPORT_DOWNLOAD_FAILURE:
      return {
        ...state,
        downloadProgress: {
          ...state.downloadProgress,
          [action.payload?.exportId]: 0
        },
        downloadErrors: {
          ...state.downloadErrors,
          [action.payload?.exportId]: action.payload
        }
      };
      
    // History Actions
    case EXCEL_EXPORT_HISTORY_FETCH_START:
      return {
        ...state,
        historyLoading: true,
        error: null
      };
      
    case EXCEL_EXPORT_HISTORY_FETCH_SUCCESS:
      return {
        ...state,
        historyLoading: false,
        error: null,
        exportHistory: action.payload.exports,
        historyPagination: action.payload.pagination
      };
      
    case EXCEL_EXPORT_HISTORY_FETCH_FAILURE:
      return {
        ...state,
        historyLoading: false,
        error: action.payload
      };
      
    // Statistics Actions
    case EXCEL_EXPORT_STATISTICS_FETCH_START:
      return {
        ...state,
        statisticsLoading: true,
        error: null
      };
      
    case EXCEL_EXPORT_STATISTICS_FETCH_SUCCESS:
      return {
        ...state,
        statisticsLoading: false,
        error: null,
        statistics: action.payload
      };
      
    case EXCEL_EXPORT_STATISTICS_FETCH_FAILURE:
      return {
        ...state,
        statisticsLoading: false,
        error: action.payload
      };
      
    // UI State Actions
    case EXCEL_EXPORT_SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload
      };
      
    case EXCEL_EXPORT_UPDATE_FIELDS:
      return {
        ...state,
        selectedFields: action.payload
      };
      
    case EXCEL_EXPORT_UPDATE_FILTERS:
      return {
        ...state,
        filters: action.payload
      };
      
    case EXCEL_EXPORT_UPDATE_OPTIONS:
      return {
        ...state,
        exportOptions: {
          ...state.exportOptions,
          ...action.payload
        }
      };
      
    // Validation Actions
    case 'EXCEL_EXPORT_VALIDATION_UPDATE':
      return {
        ...state,
        validation: action.payload
      };
      
    // Size Estimation Actions
    case 'EXCEL_EXPORT_SIZE_ESTIMATION':
      return {
        ...state,
        sizeEstimation: action.payload
      };
      
    // Filter Preset Actions
    case 'EXCEL_EXPORT_APPLY_PRESET':
      return {
        ...state,
        filters: action.payload.filters,
        activePreset: action.payload.name
      };
      
    case 'EXCEL_EXPORT_CLEAR_PRESET':
      return {
        ...state,
        activePreset: null
      };
      
    case EXCEL_EXPORT_RESET_STATE:
      return {
        ...initialState,
        // Keep some state that shouldn't be reset
        availableFields: state.availableFields,
        relationships: state.relationships
      };
      
    default:
      return state;
  }
};

export default excelExportReducer;
